import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { joiResolver } from '@hookform/resolvers/joi';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import {
  ApiButton,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ErrorInfo,
  Icon,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import schema from '../../../schema/accessArrangementRequestSchema';
import {
  AccessArrangementRequestData,
  AccessArrangementRequestDetail,
  AccessArrangementRequestSelection,
  SubjectPaper,
} from './AccessArrangementRequestModels';
import AddRequestModalContent from './AddRequestModalContent';
import EditRequestModalContent from './EditRequestModalContent';
import {
  usePostAccessArrangementRequestDetails,
  usePutAccessArrangementRequestDetails,
} from '../../../../../api/candidate/access-arrangement/@candidate/queries-services/queries';
import { useAccessArrangementRequest } from '../../../logic/useAccessArrangementRequest';

const useStyles = makeStyles()((theme) =>
({
  dialog: {
    overflow: 'hidden',
  },
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  spaceBottom: {
    marginBottom: theme.spacing(3),
  },
  radioGroupRoot: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  remarks: {
    marginBottom: theme.spacing(2),
  },
  minHeight: {
    minHeight: 125,
  },
  buttonContainer: {
    marginRight: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  addIcon: {
    verticalAlign: 'sub',
    marginRight: 10,
    color: theme.color.icon.iconInverse,
  },
  errorMsg: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
}));

const REMARKS_MAX_LENGTH = 200;

interface AccessArrangementRequestModalProps {
  isAddMode: boolean;
  modalState: boolean;
  onDialogClose: (doRefetch?: boolean) => void;
  uuid: string;
  accessArrangementRequestSelections: AccessArrangementRequestSelection;
  accessArrangementRequestDetail?: AccessArrangementRequestDetail;
}

const AccessArrangementRequestModal = ({
  isAddMode = true,
  modalState = false,
  onDialogClose = () => null,
  uuid,
  accessArrangementRequestSelections,
  accessArrangementRequestDetail,
}: AccessArrangementRequestModalProps) => {
  const formHook = useForm<AccessArrangementRequestData>({
    mode: 'onSubmit',
    resolver: joiResolver(schema(REMARKS_MAX_LENGTH)),
  });
  const {
    control,
        formState: { errors },
    handleSubmit,
        register,
        setValue,
        watch,
  } = formHook;
  const [remarksInvalid, setRemarksInvalid] = useState(true);
  const [subjectPaperInvalid, setSubjectPaperInvalid] = useState(true);
  const [remarkIndicator, setRemarkIndicator] = useState(false);
  const [selectedSubjectPapers, setSelectedSubjectPapers] = useState<SubjectPaper[]>();
  const [errorMsg, setErrorMsg] = useState<string>('');
  const { t } = useTranslation();
  const { classes } = useStyles();

  const checkSubjectPaperChange = useAccessArrangementRequest(setSubjectPaperInvalid);

  const postAccessArrangementRequestDetails = usePostAccessArrangementRequestDetails(
    () => {
      onDialogClose(true);
    },
    (err) => setErrorMsg(err?.response?.data.message)
  );

  const putAccessArrangementRequestDetails = usePutAccessArrangementRequestDetails(
    () => {
      onDialogClose(true);
    },
    (err) => setErrorMsg(err?.response?.data.message)
  );

  const getRemarkIndicator = useCallback((indicator: boolean) => setRemarkIndicator(indicator), []);

  const getSelectedSubjectPapers = useCallback(
    (subjectPapers: SubjectPaper[]) => setSelectedSubjectPapers(subjectPapers),
    []
  );

  const remarksChange = watch('remarks');
  const subjectPapersChange = watch('subjectPapers');

  useEffect(() => {
    checkSubjectPaperChange(subjectPapersChange, selectedSubjectPapers);
  }, [checkSubjectPaperChange, subjectPapersChange, selectedSubjectPapers]);

  useEffect(() => {
    if (remarkIndicator) {
      setRemarksInvalid(remarksChange ? remarksChange.length === 0 : true);
    } else {
      setRemarksInvalid(false);
    }
  }, [remarkIndicator, remarksChange]);

  const mode = isAddMode ? 'Add' : 'Edit';

  return (
    <Dialog
      id="access-arrangement-request-dialog"
      fullWidth
      open={modalState}
      maxWidth="md"
      slotProps={{
        paper: { className: classes.dialog }
    }}
    >
      <form
        className={classes.form}
        onSubmit={handleSubmit((formData: AccessArrangementRequestData) => {
          setErrorMsg('');
          if (isAddMode) {
            postAccessArrangementRequestDetails.mutate({
              uuid,
              accessArrangementRequestData: formData,
            });
          } else {
            putAccessArrangementRequestDetails.mutate({
              uuid,
              accessArrangementRequestData: formData,
            });
          }
        })}
      >
        <DialogTitle>
          {t(`accessArrangement->>AccessArrangementRequest.${mode}.ModalTitle`)}
        </DialogTitle>

        <DialogContent>
          {isAddMode ? (
            <AddRequestModalContent
              accessArrangementRequestSelections={accessArrangementRequestSelections}
              remarksMaxLength={REMARKS_MAX_LENGTH}
              control={control}
              form={formHook}
              setValue={setValue}
              watch={watch}
              getRemarkIndicator={getRemarkIndicator}
            />
          ) : (
            <>
              {accessArrangementRequestDetail && (
                <EditRequestModalContent
                  subjectPapers={
                    accessArrangementRequestSelections[
                      accessArrangementRequestDetail.modeOfAssessment.iexamsCode
                    ].subjectPapers
                  }
                  accessArrangementRequestDetail={accessArrangementRequestDetail}
                  control={control}
                  register={register}
                  setValue={setValue}
                  getSelectedSubjectPapers={getSelectedSubjectPapers}
                />
              )}
            </>
          )}
          <ErrorInfo className={classes.errorMsg} label={errorMsg} />
        </DialogContent>
        <DialogActions>
          <Box className={classes.buttonContainer}>
            <Button
              id="cancel-button"
              type="button"
              variant="text"
              onClick={() => onDialogClose()}
            >
              {t('accessArrangement->>AccessArrangementRequest.Cancel')}
            </Button>
            <ApiButton
              id="add-request-button"
              type="submit"
              status={
                isAddMode
                  ? postAccessArrangementRequestDetails.status
                  : putAccessArrangementRequestDetails.status
              }
              disabled={remarksInvalid || subjectPaperInvalid}
            >
              {isAddMode && <Icon size="M" className={classes.addIcon} name={IconName.add} />}
              {t(`accessArrangement->>AccessArrangementRequest.${mode}.Save`)}
            </ApiButton>
          </Box>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AccessArrangementRequestModal;
